// src/app/core/services/error-handler.service.ts
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ApiError } from '../../core/models/api-error.model';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private errorSubject = new BehaviorSubject<ApiError | null>(null);
  public error$ = this.errorSubject.asObservable();

  constructor(private router: Router) {}

  handleError(error: HttpErrorResponse): void {
    const apiError: ApiError = {
      code: error.status.toString(),
      message: this.getErrorMessage(error),
      details: error.error,
      timestamp: new Date(),
      path: error.url || undefined,
    };

    this.errorSubject.next(apiError);
    this.processError(apiError);
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return this.handleBadRequest(error);
      case 401:
        return 'Unauthorized access. Please log in again.';
      case 403:
        return "You don't have permission to access this resource.";
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return this.handleValidationError(error);
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  private handleBadRequest(error: HttpErrorResponse): string {
    if (error.error?.validationErrors) {
      return Object.values(error.error.validationErrors).join('. ');
    }
    return error.error?.message || 'Invalid request';
  }

  private handleValidationError(error: HttpErrorResponse): string {
    if (Array.isArray(error.error)) {
      return error.error.map((e: any) => e.message).join('. ');
    }
    return 'Validation failed';
  }

  private processError(error: ApiError): void {
    switch (error.code) {
      case '401':
        this.handleUnauthorized();
        break;
      case '403':
        this.handleForbidden();
        break;
      // Add more specific error handling as needed
    }
  }

  private handleUnauthorized(): void {
    localStorage.removeItem('token');
    this.router.navigate(['/login']);
  }

  private handleForbidden(): void {
    this.router.navigate(['/forbidden']);
  }

  clearError(): void {
    this.errorSubject.next(null);
  }
}
